import { Fragment, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { MaintenanceRef, WorkOrderListItem, WorkOrderPatch } from "../api/types";
import { WO_STATUSES, formatDate, formatWoId, isOverdue, woStatusLabel } from "../lib/workorders";

const COLUMNS = ["", "ID", "Title", "Building", "Where", "Person", "Assigned", "Status", "Due"];

export function WorkOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [building, setBuilding] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const due = searchParams.get("due") ?? "all";

  function setDue(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("due");
    else next.set("due", value);
    setSearchParams(next, { replace: true });
  }

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });
  const maintenanceQ = useQuery({
    queryKey: ["ref", "maintenance"],
    queryFn: () => api.reference.maintenance(),
  });

  const listKey = ["work-orders", { building, status, due }] as const;
  const listQ = useQuery({
    queryKey: listKey,
    queryFn: () => api.workOrders.list({ building, status, due }),
  });

  // Inline edits feel instant via an optimistic merge; the server refetch
  // (onSettled) reconciles derived fields like the assignee name.
  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: WorkOrderPatch }) =>
      api.workOrders.update(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: ["work-orders"] });
      const prev = queryClient.getQueryData<{ workOrders: WorkOrderListItem[] }>(listKey);
      if (prev) {
        queryClient.setQueryData<{ workOrders: WorkOrderListItem[] }>(listKey, {
          workOrders: prev.workOrders.map((w) => (w.id === id ? { ...w, ...patch } : w)),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(listKey, ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
  });

  const all = listQ.data?.workOrders ?? [];
  const term = q.trim().toLowerCase();
  const rows = term
    ? all.filter(
        (w) =>
          w.title.toLowerCase().includes(term) ||
          w.description.toLowerCase().includes(term) ||
          formatWoId(w.id).toLowerCase().includes(term),
      )
    : all;
  const maintenance = maintenanceQ.data?.maintenance ?? [];

  function toggleDescription(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Layout title="Work Orders">
      <div className="page-actions">
        <Link to="/work-orders/new" className="btn btn-primary btn-xl">
          + Add a new work order
        </Link>
      </div>

      <div className="filters">
        <label className="field">
          <span>Search</span>
          <DebouncedSearchInput value={q} onValueChange={setQ} />
        </label>
        <label className="field">
          <span>Building</span>
          <select value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="all">All buildings</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            {WO_STATUSES.map((s) => (
              <option key={s} value={s}>
                {woStatusLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Due</span>
          <select value={due} onChange={(e) => setDue(e.target.value)}>
            <option value="all">All</option>
            <option value="overdue">Overdue</option>
            <option value="today">Today</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
          </select>
        </label>
      </div>

      {due === "overdue" && (
        <div className="filter-banner">
          <div>
            <strong>Showing overdue work orders</strong>
            <span>
              {rows.length} result{rows.length === 1 ? "" : "s"} · past-due and not yet Completed
            </span>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setDue("all")}>
            Show all work orders
          </button>
        </div>
      )}

      <section className="card">
        {listQ.isLoading ? (
          <p className="sub">Loading…</p>
        ) : listQ.isError ? (
          <p className="sub">Could not load work orders. Please try again.</p>
        ) : rows.length === 0 ? (
          <p className="sub">No work orders match these filters.</p>
        ) : (
          <div className="table-wrap stack-md work-orders-table-wrap">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => {
                  const isExpanded = expanded.has(w.id);
                  return (
                    <Fragment key={w.id}>
                      <WorkOrderRow
                        wo={w}
                        maintenance={maintenance}
                        expanded={isExpanded}
                        onToggle={() => toggleDescription(w.id)}
                        onPatch={(patch) => updateMut.mutate({ id: w.id, patch })}
                      />
                      {isExpanded && (
                        <tr className="wo-description-row wo-description-row--desktop">
                          <td colSpan={COLUMNS.length}>
                            <div className="wo-description-panel">
                              <strong>Description</strong>
                              <p>{w.description || "No description provided."}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}

function DebouncedSearchInput({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => onValueChange(draft), 180);
    return () => window.clearTimeout(handle);
  }, [draft, onValueChange]);

  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      placeholder="Type to search..."
      autoComplete="off"
      spellCheck={false}
    />
  );
}

function WorkOrderRow({
  wo,
  maintenance,
  expanded,
  onToggle,
  onPatch,
}: {
  wo: WorkOrderListItem;
  maintenance: MaintenanceRef[];
  expanded: boolean;
  onToggle: () => void;
  onPatch: (patch: WorkOrderPatch) => void;
}) {
  const tone =
    wo.status === "completed"
      ? "done"
      : wo.status === "cancelled"
        ? "muted"
        : isOverdue(wo.status, wo.dueDate)
          ? "overdue"
          : "active";

  return (
    <tr className={`wo-row wo-row--${tone}`}>
      <td data-label="Description" className="wo-expand-cell">
        <div className="wo-expand-cell-head">
          <button
            type="button"
            className={"wo-expand-button" + (expanded ? " expanded" : "")}
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Hide" : "Show"} description for ${formatWoId(wo.id)}`}
          >
            <span aria-hidden="true" />
          </button>
        </div>
        {expanded && (
          <div className="wo-description-panel wo-description-panel--mobile">
            <strong>Description</strong>
            <p>{wo.description || "No description provided."}</p>
          </div>
        )}
      </td>
      <td data-label="ID">{formatWoId(wo.id)}</td>
      <td data-label="Title">
        <Link to={`/work-orders/${wo.id}`} className="wo-title-link">
          {wo.title}
        </Link>
      </td>
      <td data-label="Building">{wo.buildingName}</td>
      <td data-label="Where">{wo.where}</td>
      <td data-label="Person">
        <select
          className="inline-edit"
          value={wo.assignedMaintenanceId ?? ""}
          onChange={(e) =>
            onPatch({ assignedMaintenanceId: e.target.value ? Number(e.target.value) : null })
          }
          aria-label="Assigned to"
        >
          <option value="">Unassigned</option>
          {maintenance.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </td>
      <td data-label="Assigned">
        <input
          type="date"
          className="inline-edit"
          value={wo.assignedDate ?? ""}
          onChange={(e) => onPatch({ assignedDate: e.target.value || null })}
          aria-label="Assigned date"
        />
        {wo.timerStartedAt && <div className="badge badge-info">Timer started</div>}
        {!wo.assignedDate && !wo.timerStartedAt && <div className="sub">{formatDate(null)}</div>}
      </td>
      <td data-label="Status">
        <select
          className="inline-edit"
          value={wo.status}
          onChange={(e) => onPatch({ status: e.target.value })}
          aria-label="Status"
        >
          {WO_STATUSES.map((s) => (
            <option key={s} value={s}>
              {woStatusLabel(s)}
            </option>
          ))}
        </select>
      </td>
      <td data-label="Due">
        <input
          type="date"
          className="inline-edit"
          value={wo.dueDate ?? ""}
          onChange={(e) => onPatch({ dueDate: e.target.value || null })}
          aria-label="Due date"
        />
        {isOverdue(wo.status, wo.dueDate) && <div className="field-error">Overdue</div>}
      </td>
    </tr>
  );
}
